import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { getBaseAppUrl, getBaseApiUrl } from './EnvironmentManager';
import { CatalogItemVariant } from './CatalogManager';

interface ProductRequestParams {
  userId: string,
  sourceImageUrl: string;
  sourceImageId: string;
  catalogItemId: string;
  catalogItemVariantId: string;
}

const getProductApiUrl = () => {
  return `${getBaseApiUrl()}/product`;
};

export class Product {

  constructor(init?: Partial<Product>) {
    if (init) {
      Object.assign(this, init);
    }
  }

  productId?: string;
  userId?: string;
  createdDate?: string;
  updatedDate?: string;

  name?: string;
  title?: string; // For backward compatibility
  description?: string;
  prompt?: string;
  category?: string;
  catalogItemCategory?: string; // Category from catalog item for SEO URLs
  sourceImageId?: string;
  previewImageKeys?: string[];
  previewImageUrls?: string[];
  catalogItemId?: string;
  catalogItemName?: string;
  catalogItemDescription?: string;
  catalogItemVariantId?: string;
  catalogItemVariantName?: string;
  catalogItemVariant?: CatalogItemVariant;
  price?: number;
  vendorName?: string;
  vendorData?: any;
  statusTag?: string; // e.g. "PUBLIC", "FEATURED", "TEMPLATE"
  tags?: string[];
  slug?: string;
  status?: string; // e.g. "ACTIVE", "INACTIVE"
  templateProductId?: string;
  templateConfig?: {
    parameters: Array<{
      id: string;
      label: string;
      type: 'text' | 'image' | 'textarea';
      placeholder?: string;
      required: boolean;
    }>;
    promptTemplate: string;
  };

  get catalogItemFullName(): string {
    const name = this.catalogItemName?.trim() || '';
    const variant = this.catalogItemVariantName?.trim() || '';
    if (name && variant) return `${name} - ${variant}`;
    return name || variant || '';
  }

  get isPublic(): boolean {
    return this.statusTag === 'PUBLIC';
  }

  get isFeatured(): boolean {
    return this.statusTag === 'FEATURED';
  }
}


const useProductApi = () => {
  const { getAccessTokenSilently } = useAuth0();

  const generateProduct = async (params: ProductRequestParams): Promise<Product> => {
    const endpointUrl = getProductApiUrl();
    try {
      console.log('Generating product with params:', params);
      const accessToken = await getAccessTokenSilently();
      const response = await axios.post(endpointUrl, params, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        }
      });

      console.log('Product generation response:', response?.data);

      if (response?.status === 200 && response?.data?.status === 'success') {
        return new Product(response.data.data);
      } else {
        console.error('Product generation failed:', response?.data);
        throw new Error(`Product generation failed. Status: ${response?.data?.status || 'unknown'}, Message: ${response?.data?.message || 'No message provided'}`);
      }
    } catch (error: any) {
      console.error('Error generating product:', error);
      
      if (axios.isAxiosError(error)) {
        const statusCode = error.response?.status;
        const errorData = error.response?.data;
        console.error('API Error Response:', errorData);
        
        if (statusCode === 404) {
          throw new Error('Product generation service not found. Please check the API endpoint.');
        } else if (statusCode === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (statusCode === 500) {
          throw new Error(`Server error during product generation: ${errorData?.message || 'Internal server error'}`);
        }
        
        throw new Error(`Product generation failed with status ${statusCode}: ${errorData?.message || error.message}`);
      }
      
      throw new Error(`Product generation failed: ${error.message || 'Unknown error'}`);
    }
  };

  const getProducts = async (statusTag: string = 'PUBLIC', limit: number = 10, lastKey?: Record<string, any>): Promise<{ items: Array<Object>, lastKey: Record<string, any> | null }> => {
    const params = new URLSearchParams({
      statusTag: statusTag,
      limit: limit.toString(),
      lastKey: lastKey
        ? JSON.stringify(lastKey) // Encode the lastKey as a JSON string
        : "",
    });

    const endpointUrl = `${getProductApiUrl()}?${params.toString()}`;
    try {
      const response = await axios.get(endpointUrl, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response?.status === 200 && response?.data?.status === 'success' && response?.data?.data) {
        return {
          items: response.data.data.items.map((item: any) => new Product(item)),
          lastKey: response.data.data.lastKey
        };
      } else {
        throw new Error('No product data found in the response or status is not success.');
      }
    } catch (error) {
      console.error('Error calling API:', JSON.stringify(error));
      throw new Error('Failed to retrieve product data from the API');
    }
  };

  const getUserProducts = async (userId: string = "0", limit: number = 10, lastKey?: Record<string, any>): Promise<{ items: Array<Object>, lastKey: Record<string, any> | null }> => {
    const params = new URLSearchParams({
      userId: userId,
      limit: limit.toString(),
      lastKey: lastKey
        ? JSON.stringify(lastKey) // Encode the lastKey as a JSON string
        : "",
    });

    const endpointUrl = `${getProductApiUrl()}?${params.toString()}`;
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.get(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        }
      });

      if (response?.status === 200 && response?.data?.status === 'success' && response?.data?.data) {
        return {
          items: response.data.data.items.map((item: any) => new Product(item)),
          lastKey: response.data.data.lastKey
        };
      } else {
        throw new Error('No product data found in the response or status is not success.');
      }
    } catch (error) {
      console.error('Error calling API:', JSON.stringify(error));
      throw new Error('Failed to retrieve product data from the API');
    }
  };

  const getProduct = async (productId: string): Promise<Product> => {
    const endpointUrl = `${getProductApiUrl()}/${productId}`;
    try {
      const response = await axios.get(endpointUrl, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if ((response?.status === 200) && response?.data?.data) {
        return new Product(response.data.data);
      } else {
        throw new Error('No product data found in the response or status is not success.');
      }
    } catch (error: any) {
      console.error('Error calling API:', error?.message);
      throw new Error('Failed to retrieve product data from the API');
    }
  };

  /**
   * Update an existing product
   * @param updatedProduct - Full product object to update
   * @returns {Promise<Product>}
   */
  const updateProduct = async (updatedProduct: Product): Promise<Product> => {
    const endpointUrl = `${getProductApiUrl()}/${updatedProduct.productId}`;
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.put(endpointUrl, updatedProduct, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        }
      });

      if ((response?.status === 200 || response?.status === 204) && response?.data?.data) {
        return new Product(response.data.data);
      } else {
        throw new Error('Failed to update product. Response status not success.');
      }
    } catch (error) {
      console.error('Error updating product:', JSON.stringify(error));
      throw new Error('Failed to update the product.');
    }
  };

  /**
   * Delete a product by ID for the given userId
   * @param {string} productId - ID of the product to delete
   * @param {string} userId - ID of the user that owns the product
   * @returns {Promise<void>} - Resolves when the product is deleted
   */
  const deleteProduct = async (productId: string, userId: string): Promise<void> => {
    const endpointUrl = `${getProductApiUrl()}/${productId}?userId=${encodeURIComponent(userId)}`;
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.delete(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response?.status !== 200 || response?.data?.status !== 'success') {
        throw new Error('Failed to delete the product. Response status is not success.');
      }
    } catch (error) {
      console.error('Error deleting product:', JSON.stringify(error));
      throw new Error('Failed to delete the product.');
    }
  };

  /**
   * Get the full product URL
   * @param product
   * @param catalogItemCategory - Optional catalog item category to use for URL (preferred over product tags)
   * @returns
   */
  const getProductUrl = (product: Product, catalogItemCategory?: string) => {
    if (!product) { return; }

    // Prefer catalogItemCategory parameter, then product's category field, then tags, then uncategorized
    let category = catalogItemCategory || product?.category || (product?.tags && product?.tags?.length > 0 ? product.tags[0] : "uncategorized");
    const catalogItemId = product?.catalogItemVariantId || "unknown-catalogItemId";
    return `${getBaseAppUrl()}/products/${catalogItemId}/${category}/${product.productId}`;
  };

  /**
   * Get template products (products with statusTag = 'TEMPLATE')
   * Public endpoint - no authentication required
   * @returns Promise<Product[]>
   */
  const getTemplateProducts = async (): Promise<Product[]> => {
    const endpointUrl = getProductApiUrl();
    try {
      const response = await axios.get(endpointUrl, {
        params: { statusTag: 'TEMPLATE' },
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response?.status === 200 && response?.data?.status === 'success') {
        const products = response.data.data?.items || [];
        return products.map((p: any) => new Product(p));
      } else {
        console.error('Failed to fetch template products:', response?.data);
        throw new Error(`Failed to fetch template products. Status: ${response?.data?.status || 'unknown'}`);
      }
    } catch (error: any) {
      console.error('Error fetching template products:', error);
      throw error;
    }
  };

  /**
   * Get template parameters for image generation (does NOT trigger generation)
   * Frontend will use these parameters to call generateImageAsync
   * @param templateProductId - The productId of the template
   * @param parameters - Parameter values { paramId: value }
   * @returns Promise<{prompt, imageShape, imageStyle, imageModel, imageName, imageTags, base64Image, catalogItemId, catalogItemVariantId, catalogItemCategory}>
   */
  const generateFromTemplate = async (
    templateProductId: string,
    parameters: Record<string, any>
  ): Promise<{
    prompt: string;
    imageShape: string;
    imageStyle: string;
    imageModel: string;
    imageName: string;
    imageTags: string[];
    base64Image?: string;
    catalogItemId: string;
    catalogItemVariantId: string;
    catalogItemCategory: string;
  }> => {
    const endpointUrl = `${getProductApiUrl()}/${templateProductId}/generate-from-template`;
    try {
      console.log(`Getting template parameters for ${templateProductId} with parameters:`, parameters);
      const accessToken = await getAccessTokenSilently();
      const response = await axios.post(endpointUrl, { parameters }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        }
      });

      console.log('Template parameters response:', response?.data);

      if (response?.status === 200 && response?.data?.status === 'success') {
        // Backend returns parameters only (does NOT trigger image generation)
        return response.data.data;
      } else {
        console.error('Failed to get template parameters:', response?.data);
        throw new Error(`Failed to get template parameters. Status: ${response?.data?.status || 'unknown'}, Message: ${response?.data?.message || 'No message provided'}`);
      }
    } catch (error: any) {
      console.error('Error getting template parameters:', error);

      if (axios.isAxiosError(error)) {
        const statusCode = error.response?.status;
        const errorData = error.response?.data;
        console.error('API Error Response:', errorData);

        if (statusCode === 401 || statusCode === 403) {
          throw new Error('Authentication required or insufficient permissions');
        }

        throw new Error(errorData?.message || error.message || 'Failed to get template parameters');
      }

      throw error;
    }
  };

  return { generateProduct, getProducts, getUserProducts, getProduct, updateProduct, deleteProduct, getProductUrl, getTemplateProducts, generateFromTemplate };
};

export default useProductApi;
